import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import DSBox from "views/system-design/DSBox";
import DSTypography from "views/system-design/DSTypography";
import DSButton from "views/system-design/DSButton";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import DSSwitch from "views/system-design/DSSwitch";
import DSIconButton from "views/system-design/DSIconButton";
import DSChip from "views/system-design/DSChip";
import DSTable from "views/system-design/Table/DSTable";
import DSTableHead from "views/system-design/Table/DSTableHead";
import DSTableBody from "views/system-design/Table/DSTableBody";
import DSTableRow from "views/system-design/Table/DSTableRow";
import DSTableCell from "views/system-design/Table/DSTableCell";
import DSTableContainer from "views/system-design/Table/DSTableContainer";
import PageHeader from "components/PageHeader";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { storyManagementApi } from "../api/storyManagementApi";
import type { TesterStory, StoryFormData } from "../types";
import StoryFormDialog from "./StoryFormDialog";
import DeleteStoryDialog from "./DeleteStoryDialog";

const StoryManagement: React.FC = () => {
  const queryClient = useQueryClient();

  // Dialog state
  const [formOpen, setFormOpen] = useState(false);
  const [editingStory, setEditingStory] = useState<TesterStory | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingStory, setDeletingStory] = useState<TesterStory | null>(null);

  // Query for fetching all stories
  const {
    data: stories,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["stories-management"],
    queryFn: () => storyManagementApi.getAll(),
    retry: 1,
  });

  // Mutation for creating stories
  const createMutation = useMutation({
    mutationFn: (data: StoryFormData) => storyManagementApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stories-management"] });
      queryClient.invalidateQueries({ queryKey: ["stories"] });
      setFormOpen(false);
      setEditingStory(null);
    },
  });

  // Mutation for updating stories
  const updateMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<StoryFormData> & { enabled?: boolean };
    }) => storyManagementApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stories-management"] });
      queryClient.invalidateQueries({ queryKey: ["stories"] });
      setFormOpen(false);
      setEditingStory(null);
    },
  });

  // Mutation for deleting stories
  const deleteMutation = useMutation({
    mutationFn: (id: string) => storyManagementApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stories-management"] });
      queryClient.invalidateQueries({ queryKey: ["stories"] });
      setDeleteDialogOpen(false);
      setDeletingStory(null);
    },
  });

  // Handlers
  const handleCreateClick = () => {
    setEditingStory(null);
    setFormOpen(true);
  };

  const handleEditClick = (story: TesterStory) => {
    setEditingStory(story);
    setFormOpen(true);
  };

  const handleDeleteClick = (story: TesterStory) => {
    setDeletingStory(story);
    setDeleteDialogOpen(true);
  };

  const handleFormSave = (data: StoryFormData) => {
    if (editingStory) {
      updateMutation.mutate({ id: editingStory.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleToggleEnabled = (story: TesterStory) => {
    updateMutation.mutate({
      id: story.id,
      data: { enabled: !story.enabled },
    });
  };

  const handleDeleteConfirm = () => {
    if (deletingStory) {
      deleteMutation.mutate(deletingStory.id);
    }
  };

  const getSourceColor = (
    source: string,
  ): "default" | "primary" | "secondary" => {
    switch (source) {
      case "manual":
        return "primary";
      case "ai-generated":
        return "secondary";
      default:
        return "default";
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <DSBox
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <DSCircularProgress />
      </DSBox>
    );
  }

  // Error state
  if (error) {
    return (
      <DSBox sx={{ p: 3 }}>
        <PageHeader overline="Content" title="Story Management" />
        <DSTypography variant="body1" color="error">
          Failed to load stories
        </DSTypography>
      </DSBox>
    );
  }

  // Empty state
  if (!stories || stories.length === 0) {
    return (
      <DSBox sx={{ p: 3 }}>
        <PageHeader
          overline="Content"
          title="Story Management"
          subtitle="Create and manage tester stories."
          actions={
            <DSButton variant="contained" onClick={handleCreateClick}>
              Create Story
            </DSButton>
          }
        />
        <DSTypography variant="body2" color="text.secondary">
          No stories found. Create your first story.
        </DSTypography>
        <StoryFormDialog
          open={formOpen}
          onClose={() => setFormOpen(false)}
          onSave={handleFormSave}
          saving={createMutation.isPending || updateMutation.isPending}
          initialData={editingStory}
        />
      </DSBox>
    );
  }

  // Main table view
  return (
    <DSBox sx={{ p: 3 }}>
      <PageHeader
        overline="Content"
        title="Story Management"
        subtitle="Create and manage tester stories."
        actions={
          <DSButton variant="contained" onClick={handleCreateClick}>
            Create Story
          </DSButton>
        }
      />

      <DSTableContainer>
        <DSTable>
          <DSTableHead>
            <DSTableRow>
              <DSTableCell sx={{ fontWeight: "bold" }}>Name</DSTableCell>
              <DSTableCell sx={{ fontWeight: "bold" }}>Description</DSTableCell>
              <DSTableCell sx={{ fontWeight: "bold" }}>Source</DSTableCell>
              <DSTableCell sx={{ fontWeight: "bold" }}>Enabled</DSTableCell>
              <DSTableCell sx={{ fontWeight: "bold" }}>Actions</DSTableCell>
            </DSTableRow>
          </DSTableHead>
          <DSTableBody>
            {stories.map((story) => (
              <DSTableRow key={story.id}>
                <DSTableCell sx={{ fontWeight: "bold" }}>
                  {story.name}
                </DSTableCell>
                <DSTableCell
                  sx={{
                    maxWidth: "400px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {story.description}
                </DSTableCell>
                <DSTableCell>
                  <DSChip
                    label={story.source}
                    color={getSourceColor(story.source)}
                    size="small"
                  />
                </DSTableCell>
                <DSTableCell>
                  <DSSwitch
                    checked={story.enabled}
                    onChange={() => handleToggleEnabled(story)}
                  />
                </DSTableCell>
                <DSTableCell>
                  <DSIconButton
                    onClick={() => handleEditClick(story)}
                    size="small"
                  >
                    <EditIcon fontSize="small" />
                  </DSIconButton>
                  <DSIconButton
                    onClick={() => handleDeleteClick(story)}
                    size="small"
                  >
                    <DeleteIcon fontSize="small" />
                  </DSIconButton>
                </DSTableCell>
              </DSTableRow>
            ))}
          </DSTableBody>
        </DSTable>
      </DSTableContainer>

      <StoryFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSave={handleFormSave}
        saving={createMutation.isPending || updateMutation.isPending}
        initialData={editingStory}
      />

      <DeleteStoryDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        deleting={deleteMutation.isPending}
        storyName={deletingStory?.name || ""}
      />
    </DSBox>
  );
};

export default StoryManagement;
